import React, { useEffect, useMemo, useState } from 'react';
import { Link as Anchor, useLocation } from 'react-router-dom';
import './StoreContent.css';
import baseURL from '../../Components/url';
import { guardarSiteConfig, obtenerNombreTiendaGuardado } from '../../Components/siteConfig';

const CONTENT_MAP = {
    '/soporte': {
        title: 'Soporte',
        field: 'supportPageContent',
    },
    '/entregas': {
        title: 'Entregas',
        field: 'deliveryPageContent',
    },
    '/devoluciones': {
        title: 'Devoluciones',
        field: 'returnsPageContent',
    },
    '/terminos-y-condiciones': {
        title: 'Términos y condiciones',
        field: 'termsPageContent',
    },
};

function renderContentLines(content) {
    return String(content || '')
        .split('\n')
        .map((line) => line.trimEnd())
        .filter((line, index, lines) => line !== '' || (index > 0 && lines[index - 1] !== ''));
}

export default function StoreContent() {
    const location = useLocation();
    const [config, setConfig] = useState(null);
    const [contact, setContact] = useState(null);
    const [loading, setLoading] = useState(true);
    const pageConfig = useMemo(() => CONTENT_MAP[location.pathname] || CONTENT_MAP['/soporte'], [location.pathname]);
    const storeName = config?.nombreTienda?.trim() || obtenerNombreTiendaGuardado() || 'Tienda';

    useEffect(() => {
        fetch(`${baseURL}/configGet.php?ts=${Date.now()}`, {
            cache: 'no-store',
        })
            .then((response) => response.json())
            .then((data) => {
                if (data.config) {
                    guardarSiteConfig(data.config);
                    setConfig(data.config);
                }
            })
            .catch((error) => {
                console.error('Error loading page content:', error);
            })
            .finally(() => setLoading(false));

        fetch(`${baseURL}/contactoGet.php`, {
            method: 'GET',
            cache: 'no-store',
        })
            .then((response) => response.json())
            .then((data) => {
                const latestContact = Array.isArray(data.contacto) ? [...data.contacto].reverse()[0] : null;
                setContact(latestContact || null);
            })
            .catch((error) => {
                console.error('Error loading support contact data:', error);
            });
    }, []);

    const content = String(config?.[pageConfig.field] || '').trim();
    const lines = renderContentLines(content);
    const isSupportPage = location.pathname === '/soporte';

    return (
        <section className='storeContentPage'>
            <div className='storeContentCard'>
                <span className='storeContentEyebrow'>{storeName}</span>
                <div className='storeContentLinks'>
                    <Anchor to='/soporte'>Soporte</Anchor>
                    <Anchor to='/entregas'>Entregas</Anchor>
                    <Anchor to='/devoluciones'>Devoluciones</Anchor>
                    <Anchor to='/terminos-y-condiciones'>Términos y condiciones</Anchor>
                </div>

                <h1>{pageConfig.title}</h1>
                {isSupportPage ? (
                    <div className='storeSupportPanel'>
                        <p className='storeSupportLead'>¿Necesitas ayuda con un pedido o tienes una pregunta sobre la tienda? Usa una de estas opciones.</p>

                        <div className='storeSupportActions'>
                            {contact?.telefono ? (
                                <a
                                    href={`https://wa.me/${String(contact.telefono).replace(/\D/g, '')}`}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='storeSupportAction'
                                >
                                    Soporte por WhatsApp
                                </a>
                            ) : null}
                            {contact?.email ? (
                                <a
                                    href={`mailto:${contact.email}`}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='storeSupportAction'
                                >
                                    Soporte por email
                                </a>
                            ) : null}
                        </div>

                        <div className='storeSupportBody'>
                            <p>Usa esta sección para ayuda con pedidos, entregas, pagos por WhatsApp o consultas generales.</p>
                            <p>Si ya hiciste un pedido, menciona el número de pedido para que podamos ayudarte más rápido.</p>
                        </div>
                    </div>
                ) : loading ? (
                    <p className='storeContentEmpty'>Cargando contenido...</p>
                ) : lines.length > 0 ? (
                    <div className='storeContentBody'>
                        {lines.map((line, index) => (
                            <p key={`${pageConfig.field}-${index}`}>{line === '' ? '\u00A0' : line}</p>
                        ))}
                    </div>
                ) : (
                    <p className='storeContentEmpty'>Esta página todavía no fue configurada desde el dashboard.</p>
                )}
            </div>
        </section>
    );
}
